/**
 * Rentals — Fresh take on the Rentals third-party page.
 *
 * Copied from materialPivot.js with Rentals-specific cost types and config.
 * Key differences from rentals.js:
 *   1. Uses buildEtcLedger() instead of setUpUserObject()
 *   2. Every DB entry stays as an individual record
 *   3. Multi-entry EtcPopover support
 *   4. Correct totals via ledger sums + calculateColumnTotals()
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { View, Text, TextInput, StyleSheet, Pressable, Icon2, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom, selectedVersionAtom, jobUserDataAtom, phasedForecastAtom,
    jobOpenCommitmentsAtom, jobCostDetailAtom, isLiveVersionAtom, jobPhaseCodesAtom,
    jobUserDataPrevAtom, jobOpenCommitmentsPrevAtom, jobBillingsPrevAtom,
    jobCostDetailPrevAtom, selectedVersionLockedAtom, dataSourceVersionAtom, selectedWIPAtom
} from '../store/company';
import {
    updateUserDataArray, deleteUserData, loadUserData,
    f, fNum, pNum, sum, cN, loadJobOpenCommitmentsCombined, loadCostHistoryCombined
} from '../db/company';
import moment from 'moment';
import { buildColumnTree, calculateColumnTotals } from '../helpers/columnTreeBuilder';
import { buildEtcLedger, getEntryLevel, rekeyEtcMapByColumns } from '../helpers/etcLedger';
import { createBudgetResolver, computePageBudget, computeGrandVariance } from '../helpers/budgetResolver';
import { loadResourceCodeTables, buildResourceDropdownFromDb } from '../helpers/resourceCodeDb';
import PivotSelector, { pivotLabels } from './PivotSelector';
import VarianceWeekSelector from './VarianceWeekSelector';
import { ThirdPartyRow, ThirdPartyRowCollapsable, ThirdPartyRowContext } from './ThirdPartyRow';
import { ReversedVoucherLegend } from './reversedVoucher';
import { useTapInvoiceViewer, usePoMatchStatus } from './tapInvoiceViewer';
import EtcPopoverHost, { PopoverBreadcrumbContext } from './EtcPopoverHost';
import AuditHistoryPanel from './auditHistoryPanel';

// ── constants ──────────────────────────────────────────────────────────

const availablePivotColumns = ['Phase_Code', 'Discipline', 'Resource_Code', 'Supplier_Name', 'PO_Number', 'Cost_Type_Code', 'Line_ID'];

const tDescriptions = {
    '52200': 'Outside Equipment Rental',
};

const PAGE_COST_TYPES = Object.keys(tDescriptions);

const indentColors = {
    1: '#e8edf2',
    2: '#eef1f6',
    3: '#f3f5f9',
    4: '#f8f9fb',
};

const coCodeType = ['52200'];

const costTypeDescriptions = [];

// Map L4 phase codes (8 digits) → L3 (6 digits) so cost history aligns with budget.
// Only applies to labor (51xxx) and equipment (52xxx) cost types per JDE convention.
const mapPhaseL4toL3 = (costCode, costType) => {
    if (!costCode) return costCode;
    const code = String(costCode).trim();
    const ct = String(costType || '').trim();
    if ((ct.startsWith('51') || ct.startsWith('52')) && code.length > 6) {
        return code.slice(0, 6);
    }
    return code;
};

// ── Collect descendant entries from a tree node's children ──
const collectChildEntries = (node, entriesByBreadcrumb) => {
    const childEntries = [];
    const walk = (n) => {
        if (!n?.rows || n.rows === false) return;
        Object.values(n.rows).forEach(child => {
            const bc = child.data?.breadcrumb;
            if (bc && entriesByBreadcrumb[bc]) {
                childEntries.push(...entriesByBreadcrumb[bc]);
            }
            walk(child);
        });
    };
    walk(node);
    return childEntries;
};

// ── PivotRowCollapsable ──
const PivotRowCollapsable = React.memo(({ data }) => {
    const ctx = React.useContext(ThirdPartyRowContext);
    const popoverBreadcrumb = React.useContext(PopoverBreadcrumbContext);
    const { entriesByBreadcrumb = {}, setPopoverBreadcrumb, pivotPopoverProps, descriptionVisible } = ctx;
    const allEntries = data?.data?.breadcrumb ? (entriesByBreadcrumb[data.data.breadcrumb] || []) : [];
    const entries = allEntries.filter(e => e.amount !== 0 || e.description || e.notes);
    const allChildEntries = useMemo(() => collectChildEntries(data, entriesByBreadcrumb), [data, entriesByBreadcrumb]);
    const childEntries = allChildEntries.filter(e => e.amount !== 0 || e.description || e.notes);
    const totalCount = entries.length + childEntries.length;
    const isOpen = popoverBreadcrumb === data?.data?.breadcrumb;
    const hasBreadcrumb = !!data?.data?.breadcrumb;

    return (
        <View style={{ position: 'relative' }}>
            <ThirdPartyRowCollapsable data={data} />
            {hasBreadcrumb && (
                <>
                    {totalCount >= 2 && (
                        <Pressable
                            onPress={() => setPopoverBreadcrumb(isOpen ? null : data.data.breadcrumb)}
                            style={{
                                position: 'absolute', top: -12, zIndex: 5, cursor: 'pointer',
                                left: descriptionVisible ? 1885 : 885,
                            }}
                        >
                            <View style={{
                                backgroundColor: isOpen ? '#1d4ed8' : '#3b82f6',
                                borderRadius: 3,
                                padding: '1px 5px', marginLeft: 3, whiteSpace: 'nowrap', lineHeight: 1.2,
                                boxShadow: isOpen ? '0 0 0 2px rgba(59,130,246,0.3)' : '0 1px 2px rgba(0,0,0,0.12)',
                                transition: 'all 0.15s ease',
                            }}>
                                <Text style={{ fontSize: 8, fontWeight: '700', color: 'white', whiteSpace: 'nowrap' }}>{totalCount} val.</Text>
                            </View>
                        </Pressable>
                    )}
                </>
            )}
        </View>
    );
});

// ── component ──────────────────────────────────────────────────────────

const Rentals = () => {
    // ── atoms ──
    const [userData, setUserData] = useAtom(jobUserDataAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [jobCostHistory] = useAtom(jobCostDetailAtom);
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [userDataPrev] = useAtom(jobUserDataPrevAtom);
    const [jobOpenCommitmentsPrev] = useAtom(jobOpenCommitmentsPrevAtom);
    const [jobCostHistoryPrev] = useAtom(jobCostDetailPrevAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [phased] = useAtom(phasedForecastAtom);
    // Scanned vendor-invoice viewer + PO-match rollup (shared with Billing).
    const { openTapDoc, viewerNode } = useTapInvoiceViewer();
    const poStatus = usePoMatchStatus(selectedJob);
    const [phaseDescriptions] = useAtom(jobPhaseCodesAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);

    // ── local state ──
    const [objectMap, setObjectMap] = useState({});
    const [objectMapPrev, setObjectMapPrev] = useState({});
    const [openCommitments, setOpenCommitments] = useState([]);
    const [costHistory, setCostHistory] = useState([]);
    const [openCommitmentsPrev, setOpenCommitmentsPrev] = useState([]);
    const [costHistoryPrev, setCostHistoryPrev] = useState([]);
    const [expandedBreadcrumbs, setExpandedBreadcrumbs] = useState({});
    const [phaseDayZero, setPhaseDayZero] = useState({});
    const [dayZeroChangeOrderPhases, setDayZeroChangeOrderPhases] = useState([]);
    const [titleDescriptions, setTitleDescriptions] = useState(tDescriptions);
    const [resourceDescriptions, setResourceDescriptions] = useState({});
    const [dbResourceCodes, setDbResourceCodes] = useState({ byCode: {}, codesByCostType: {}, mappingByCode: {} });
    const [descriptionVisible, setDescriptionVisible] = useState(false);
    const [collapseAll, setCollapseAll] = useState(false);
    const [collapseNumber, setCollapseNumber] = useState(0);
    const [activeCostTypes, setActiveCostTypes] = useState(() => {
        const init = {}; Object.keys(tDescriptions).forEach(k => init[k] = true); return init;
    });
    const [sortedUserData, setSortedUserData] = useState([]);
    const [deletingRows, setDeletingRows] = useState(new Set());
    const [isExporting, setIsExporting] = useState(false);
    const focusedId = useRef(null);

    // Grand total state
    const [totalCurrentBudget, setTotalCurrentBudget] = useState(0);
    const [actualTotalTotal, setActualTotalTotal] = useState(0);
    const [openTotalTotal, setOpenTotalTotal] = useState(0);
    const [etcTotalTotal, setEtcTotalTotal] = useState(0);
    const [totalTotalTotal, setTotalTotalTotal] = useState(0);
    const [varianceBudgetTotal, setVarianceBudgetTotal] = useState(0);

    // Variance weeks
    const [varianceWeeks, setVarianceWeeks] = useState([]);
    const [weekTotalMaps, setWeekTotalMaps] = useState({});
    const [weekLoading, setWeekLoading] = useState({});
    const [weekDisplayMode, setWeekDisplayMode] = useState({});
    const [prevGrandTotal, setPrevGrandTotal] = useState(0);

    // ETC Popover state
    const [popoverBreadcrumb, setPopoverBreadcrumb] = useState(null);
    const [entriesByBreadcrumb, setEntriesByBreadcrumb] = useState({});
    const [rebuilding, setRebuilding] = useState(false);

    // Budget map — flat { breadcrumb → budget } built from tree, single source of truth for display
    const [budgetMap, setBudgetMap] = useState({});

    // Ledger reference for tree enrichment
    const ledgerRef = useRef({ userEtcMap: {}, entriesByBreadcrumb: {} });

    // ── drill columns ──
    const [drillColumns, setDrillColumns] = useState(
        phased === 'phased'
            ? ['Phase_Code', 'Supplier_Name', 'PO_Number', 'Line_ID']
            : ['Supplier_Name', 'PO_Number', 'Line_ID']
    );
    useEffect(() => {
        setDrillColumns(phased === 'phased'
            ? ['Phase_Code', 'Supplier_Name', 'PO_Number', 'Line_ID']
            : ['Supplier_Name', 'PO_Number', 'Line_ID']);
    }, [phased]);

    const path = useMemo(() => [...drillColumns, 'PO_Detail'], [drillColumns]);

    // ── styles (cached) ──
    const _sc = useRef(new Map());
    const _createStyles = (w = null) => StyleSheet.create({
        edit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: w || 100, padding: 2, paddingLeft: 8, textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        notEdit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: w || 100, padding: 2, maxWidth: w || 100, textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        firstNotEdit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: '100%', padding: 2, maxWidth: '100%', textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        title: { width: w || 100, alignItems: 'center', fontSize: 10, fontWeight: '600', padding: '6px 4px', textAlign: 'right', justifyContent: 'flex-end', borderBottom: '1px solid #e5e7eb', backgroundColor: 'transparent', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.6 },
    });
    const s = (w = null) => {
        if (_sc.current.has(w)) return _sc.current.get(w);
        const r = _createStyles(w); _sc.current.set(w, r); return r;
    };

    // ── OC / CH preprocessing ──
    useEffect(() => {
        const oC = [];
        (jobOpenCommitments || []).forEach(e => {
            const d = { ...e };
            if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
            if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
            d.OC = true;
            d.Supplier_Name = d.Supplier_AB_Name;
            d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            d.Line_ID = d.PO_Line_Number || '';
            oC.push(d);
        });
        setOpenCommitments(oC);
    }, [jobOpenCommitments]);

    useEffect(() => {
        const jCH = [];
        (jobCostHistory || []).forEach(d => {
            const c = { ...d };
            c.CH = true;
            c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            c.Cost_Code = mapPhaseL4toL3(d.Cost_Code, d.Cost_Type);
            jCH.push(c);
        });
        setCostHistory(jCH);
    }, [jobCostHistory]);

    // Previous version OC/CH
    useEffect(() => {
        const oC = [];
        (jobOpenCommitmentsPrev || []).forEach(e => {
            const d = { ...e };
            if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
            if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
            d.OC = true;
            d.Supplier_Name = d.Supplier_AB_Name;
            d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            d.Line_ID = d.PO_Line_Number || '';
            oC.push(d);
        });
        setOpenCommitmentsPrev(oC);
    }, [jobOpenCommitmentsPrev]);

    useEffect(() => {
        const jCH = [];
        (jobCostHistoryPrev || []).forEach(d => {
            const c = { ...d };
            c.CH = true;
            c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            c.Cost_Code = mapPhaseL4toL3(d.Cost_Code, d.Cost_Type);
            jCH.push(c);
        });
        setCostHistoryPrev(jCH);
    }, [jobCostHistoryPrev]);

    // ── build discipline map ──
    const disciplineMap = useMemo(() => {
        const map = {};
        (userData || []).forEach(d => {
            if (d?.Forecast_Tab === 'Productivity Tracker' && d?.Forecast_Field_Name === 'discipline') {
                map[d?.Forecast_Row_ID] = d?.Forecast_Value;
            }
        });
        return map;
    }, [userData]);

    const reverseDisciplineMap = useMemo(() => {
        const rev = {};
        Object.entries(disciplineMap).forEach(([phase, disc]) => {
            if (!disc) return;
            if (!rev[disc]) rev[disc] = [];
            rev[disc].push(phase);
        });
        return rev;
    }, [disciplineMap]);

    // ── main data processing pipeline ──
    const processDebounceRef = useRef(null);

    useEffect(() => {
        setRebuilding(true);
        if (processDebounceRef.current) clearTimeout(processDebounceRef.current);
        processDebounceRef.current = setTimeout(() => {
            if (!userData || !phaseDescriptions) { setRebuilding(false); return; }

            const ledger = buildEtcLedger(userData, 'Rentals', drillColumns, disciplineMap);
            ledgerRef.current = ledger;
            setEntriesByBreadcrumb(ledger.entriesByBreadcrumb);
            setPhaseDayZero(ledger.dayZeroBudget);
            const pageBudget = computePageBudget(ledger.costTypeBudget, PAGE_COST_TYPES);
            setTotalCurrentBudget(fNum(pageBudget));
            setDayZeroChangeOrderPhases(Object.values(ledger.costCodeSeeds));

            const userRows = ledger.allEntries
                .filter(e => e.amount !== 0 || e.notes || e.description)
                .map(e => {
                    const d = e.depth || 1;
                    return {
                        ...e._row,
                        rowId: e._row?.Forecast_Row_ID,
                        Forecast_Row_ID: e.breadcrumb,
                        Forecast_Tab: e._row?.Forecast_Tab || 'Rentals',
                        Phase_Code: e.Phase_Code,
                        Supplier_Name: d >= 2 ? e.Supplier_Name : '',
                        PO_Number: d >= 3 ? e.PO_Number : '',
                        Line_ID: d >= 4 ? (e._row?.Line_ID || '') : '',
                        Cost_Type_Code: e.Cost_Type_Code,
                        Value_Amount: e.amount,
                    };
                });

            const jOC = openCommitments.length ? openCommitments : [];
            const jCH = costHistory.length ? costHistory : [];
            const dzPhases = Object.values(ledger.costCodeSeeds);
            const d = jOC.length && jCH.length ? [...jOC, ...jCH] : jOC.length ? jOC : jCH;
            const d2 = dzPhases.length && d.length ? [...d, ...dzPhases] : d.length ? d : dzPhases;
            const threeDataSources = d2.length && userRows.length ? [...d2, ...userRows] : d2.length ? d2 : userRows;

            const anyCtActive = Object.values(activeCostTypes).some(Boolean);
            const filtered = anyCtActive
                ? threeDataSources.filter(r => !r.Cost_Type_Code || (tDescriptions[r.Cost_Type_Code] && activeCostTypes[r.Cost_Type_Code]))
                : threeDataSources;

            // Build Vendor+PO → Resource_Code map from cost history / OC so legacy
            // user ETC entries saved without Resource_Code can inherit one.
            const vendorPoToResource = {};
            [...jOC, ...jCH].forEach(r => {
                const vendor = r.Supplier_Name || r.Supplier_AB_Name || '';
                const po = r.PO_Number || '';
                const code = r.Rental_Equipment_Classification_Tier_Name || r.Equipment_Classification_Tier_Name || '';
                if (vendor && po && code) {
                    const key = vendor + '^' + po;
                    if (!vendorPoToResource[key]) vendorPoToResource[key] = code;
                }
            });
            filtered.forEach(row => {
                if (!row.Discipline) row.Discipline = disciplineMap[row.Phase_Code] || '';
                // Preserve Resource_Code if already set (e.g. user ETC rows carry it from the ledger)
                if (!row.Resource_Code) row.Resource_Code = row.Rental_Equipment_Classification_Tier_Name || row.Equipment_Classification_Tier_Name || '';
                // Legacy fallback: infer from cost history via Vendor+PO
                if (!row.Resource_Code && row.Supplier_Name && row.PO_Number) {
                    row.Resource_Code = vendorPoToResource[row.Supplier_Name + '^' + row.PO_Number] || '';
                }
            });

            // Build equipment tier code → description from OC / cost history so the
            // Resource pivot renders "CODE - Description" (e.g. "TR-275HP - Trencher
            // - 226-275 HP") instead of the bare tier code.
            const resDesc = {};
            [...jOC, ...jCH].forEach(r => {
                const code = r.Rental_Equipment_Classification_Tier_Name || r.Equipment_Classification_Tier_Name || '';
                const desc = r.Equipment_Classification_Tier_Name_Description || '';
                if (code && desc && desc !== code) resDesc[code] = desc;
            });
            // Merge DB-uploaded descriptions so pivot renders "CODE - Description"
            const dbDesc = {};
            Object.entries(dbResourceCodes.byCode || {}).forEach(([c, info]) => {
                if (info?.description) dbDesc[c] = info.description;
            });
            setResourceDescriptions(prev => {
                const merged = { ...prev, ...resDesc, ...dbDesc };
                const changed = Object.keys(merged).some(k => merged[k] !== prev[k])
                    || Object.keys(merged).length !== Object.keys(prev).length;
                return changed ? merged : prev;
            });

            const drillPath = path.filter(p => p !== 'PO_Detail');
            // Re-key userEtcMap so tree's fresh breadcrumb (with enriched Resource_Code) matches
            const rekeyedEtcMap = rekeyEtcMapByColumns(filtered, ledger.userEtcMap, drillPath);
            let tree = buildColumnTree(filtered, drillPath, rekeyedEtcMap, expandedBreadcrumbs, ledger.dayZeroBudget);
            if (drillPath[0] === 'Resource_Code' && tree['']) {
                const n = tree[''];
                n.data.key = 'Unassigned';
                tree['Unassigned'] = n;
                delete tree[''];
            }

            enrichTreeWithEntries(tree, ledger.entriesByBreadcrumb);

            const budgetResolver = createBudgetResolver({
                dayZeroBudget: ledger.dayZeroBudget,
                costTypeBudget: ledger.costTypeBudget,
                budgetByPhaseCt: ledger.budgetByPhaseCt,
                budgetByCtPhase: ledger.budgetByCtPhase,
                budgetByResource: ledger.budgetByResource,
                budgetByResourceCt: ledger.budgetByResourceCt,
                drillColumns,
                pageCostTypes: PAGE_COST_TYPES,
                reverseDisciplineMap,
            });

            tree = calculateAllTotals(tree, pageBudget, budgetResolver);

            // Walk tree → flat budgetMap (single source of truth for ThirdPartyRow)
            const BUDGET_PATHS = new Set(['Phase_Code', 'Cost_Type_Code', 'Discipline', 'Resource_Code']);
            const bMap = {};
            const walkBudget = (nodes) => {
                Object.values(nodes).forEach(node => {
                    if (node.data?.breadcrumb && node.data.resolvedBudget != null && node.data.resolvedBudget !== '' && BUDGET_PATHS.has(node.data.path)) {
                        bMap[node.data.breadcrumb] = node.data.resolvedBudget;
                    }
                    if (node.rows) walkBudget(node.rows);
                });
            };
            walkBudget(tree);
            setBudgetMap(bMap);
            setObjectMap(tree);

            const sorted = [...userData]
                .filter(row => row.Forecast_Tab === 'Rentals' && row.Value_Amount)
                .sort((a, b) => (a.Forecast_User_Data_GUID + '').localeCompare(b.Forecast_User_Data_GUID + ''));
            setSortedUserData(sorted);
            setRebuilding(false);

        }, 100);
    }, [userData, openCommitments, costHistory, drillColumns, activeCostTypes, phaseDescriptions, disciplineMap, dbResourceCodes]);

    const enrichTreeWithEntries = (tree, entriesByBC) => {
        const walk = (node) => {
            if (!node || !node.data) return;
            node.data.indent = (node.data.indent || 0) * 2;
            const bc = node.data.breadcrumb;
            if (bc && entriesByBC[bc]) {
                node.data.etcEntries = entriesByBC[bc];
                const entries = entriesByBC[bc];
                if (entries.length > 0) {
                    const best = entries.find(e => e.description) || entries.find(e => e.notes) || entries[0];
                    if (best.description) node.data.description = best.description;
                    if (best.notes) node.data.notes = best.notes;
                    if (best.title) node.data.title = best.title;
                    if (node.data.detail) {
                        node.data.detail = {
                            ...node.data.detail,
                            Description: best.description || node.data.detail.Description || '',
                            Notes: best.notes || node.data.detail.Notes || '',
                            Row_Label: best.title || node.data.detail.Row_Label || '',
                        };
                        if (best.guid && !node.data.detail.Forecast_User_Data_GUID) {
                            node.data.detail = {
                                ...node.data.detail,
                                Forecast_User_Data_GUID: best.guid,
                                Forecast_Row_ID: best._row?.Forecast_Row_ID || node.data.detail.Forecast_Row_ID,
                            };
                        }
                    }
                }
            }
            if (node.rows && node.rows !== false) {
                Object.values(node.rows).forEach(walk);
            }
        };
        Object.values(tree).forEach(walk);
    };

    const calculateAllTotals = (tree, freshBudget = null, budgetResolver = null) => {
        let actualTT = 0, openTT = 0, etcTT = 0, totalTT = 0;
        const resolver = budgetResolver || createBudgetResolver({
            dayZeroBudget: phaseDayZero || {},
            costTypeBudget: {},
            budgetByPhaseCt: {},
            budgetByCtPhase: {},
            drillColumns,
            pageCostTypes: PAGE_COST_TYPES,
            reverseDisciplineMap,
        });
        Object.values(tree).forEach(costType => {
            calculateColumnTotals(costType, resolver);
            actualTT += pNum(costType.data?.actualTotal);
            openTT += pNum(costType.data?.openTotal);
            etcTT += pNum(costType.data?.etcTotal);
            totalTT += pNum(costType.data?.totalTotal);
        });
        setActualTotalTotal(fNum(actualTT));
        setOpenTotalTotal(fNum(openTT));
        setEtcTotalTotal(fNum(etcTT));
        setTotalTotalTotal(fNum(totalTT));
        const budget = freshBudget != null ? freshBudget : pNum(totalCurrentBudget);
        setVarianceBudgetTotal(fNum(computeGrandVariance(budget, totalTT)));
        return tree;
    };

    // ── Load user data ──
    useEffect(() => {
        if (selectedJob && selectedVersion) {
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then(data => {
                setUserData(data?.data);
            });
        }
    }, [selectedJob, selectedVersion, phased]);

    // ── Load admin-managed Resource Code tables (version-scoped, BU_ID='all') ──
    useEffect(() => {
        if (!selectedVersion) return;
        loadResourceCodeTables({ forecastGuid: selectedVersion }).then(setDbResourceCodes);
    }, [selectedVersion]);

    // ── Variance week loading ──
    const getWeekLabel = (guid) => {
        if (selectedWIP && guid === selectedWIP.Version_GUID) return 'WIP ' + moment(selectedWIP.Version_Start_DateTime).format('MMM D');
        if (versions?.length) {
            const v = versions.find(v => v.Version_GUID === guid);
            if (v) return moment(v.Version_Start_DateTime).format('MMM D');
        }
        return '';
    };

    useEffect(() => {
        if (!varianceWeeks.length || !selectedJob) return;
        const otherWeeks = varianceWeeks.filter(g => g !== selectedWIP?.Version_GUID);

        const loadWeekVersion = async (guid) => {
            setWeekLoading(prev => ({ ...prev, [guid]: true }));
            const [udRes, ocRes, chRes] = await Promise.all([
                loadUserData({ id: selectedJob, forecastGuid: guid }),
                loadJobOpenCommitmentsCombined({ id: selectedJob, versionGuid: guid }),
                loadCostHistoryCombined({ id: selectedJob, versionGuid: guid }),
            ]);
            const weekUD = udRes?.data || [];
            const weekOC = (ocRes?.data || []).map(e => {
                const d = { ...e };
                if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
                if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
                d.OC = true; d.Supplier_Name = d.Supplier_AB_Name; d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number; d.Line_ID = d.PO_Line_Number || '';
                return d;
            });
            const weekCH = (chRes?.data || []).map(d => { const c = { ...d }; c.CH = true; c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number; return c; });

            const weekLedger = buildEtcLedger(weekUD, 'Rentals', drillColumns, disciplineMap);
            const weekUserRows = weekLedger.allEntries.filter(e => e.amount !== 0).map(e => ({ ...e._row, Phase_Code: e.Phase_Code, Supplier_Name: e.Supplier_Name, PO_Number: e.PO_Number, Cost_Type_Code: e.Cost_Type_Code }));
            const combined = [...weekOC, ...weekCH, ...weekUserRows];
            // Filter to page cost types (match current tree filtering)
            const filtered = combined.filter(r => !r.Cost_Type_Code || tDescriptions[r.Cost_Type_Code]);
            filtered.forEach(row => {
                if (!row.Discipline) row.Discipline = disciplineMap[row.Phase_Code] || '';
                if (!row.Resource_Code) row.Resource_Code = row.Rental_Equipment_Classification_Tier_Name || '';
            });
            const drillPath = path.filter(p => p !== 'PO_Detail');
            const weekTree = buildColumnTree(filtered, drillPath, weekLedger.userEtcMap, {}, weekLedger.dayZeroBudget);

            let totalTT = 0;
            const etcMap = {};
            Object.values(weekTree).forEach(node => {
                calculateColumnTotals(node, weekLedger.dayZeroBudget);
                totalTT += pNum(node.data?.totalTotal);
            });
            const walkMap = (obj) => {
                Object.values(obj).forEach(row => {
                    etcMap[row.data?.breadcrumb] = isNaN(row.data?.totalTotal) ? 0 : pNum(row.data?.totalTotal);
                    if (row.rows && row.rows !== false) walkMap(row.rows);
                });
            };
            walkMap(weekTree);
            return { map: etcMap, grandTotal: totalTT };
        };

        if (selectedWIP && varianceWeeks.includes(selectedWIP.Version_GUID)) {
            const prevOC = openCommitmentsPrev.length ? openCommitmentsPrev : [];
            const prevCH = costHistoryPrev.length ? costHistoryPrev : [];
            if (userDataPrev) {
                const prevLedger = buildEtcLedger(userDataPrev, 'Rentals', drillColumns, disciplineMap);
                const prevUserRows = prevLedger.allEntries.filter(e => e.amount !== 0).map(e => ({ ...e._row, Phase_Code: e.Phase_Code, Supplier_Name: e.Supplier_Name, PO_Number: e.PO_Number, Cost_Type_Code: e.Cost_Type_Code }));
                const combined = [...prevOC, ...prevCH, ...prevUserRows];
                // Filter to page cost types (match current tree filtering)
                const filtered = combined.filter(r => !r.Cost_Type_Code || tDescriptions[r.Cost_Type_Code]);
                filtered.forEach(row => {
                    if (!row.Discipline) row.Discipline = disciplineMap[row.Phase_Code] || '';
                    if (!row.Resource_Code) row.Resource_Code = row.Rental_Equipment_Classification_Tier_Name || '';
                });
                const drillPath = path.filter(p => p !== 'PO_Detail');
                const prevTree = buildColumnTree(filtered, drillPath, prevLedger.userEtcMap, {}, prevLedger.dayZeroBudget);
                let totalTT = 0;
                const prevMap = {};
                Object.values(prevTree).forEach(node => {
                    calculateColumnTotals(node, prevLedger.dayZeroBudget);
                    totalTT += pNum(node.data?.totalTotal);
                });
                const walkMap = (obj) => {
                    Object.values(obj).forEach(row => {
                        prevMap[row.data?.breadcrumb] = isNaN(row.data?.totalTotal) ? 0 : pNum(row.data?.totalTotal);
                        if (row.rows && row.rows !== false) walkMap(row.rows);
                    });
                };
                walkMap(prevTree);
                setObjectMapPrev(prevMap);
                setPrevGrandTotal(totalTT);
            }
        }

        Promise.all(otherWeeks.map(async guid => {
            const result = await loadWeekVersion(guid);
            return { guid, ...result };
        })).then(results => {
            const maps = {};
            results.forEach(({ guid, map, grandTotal }) => { maps[guid] = { map, grandTotal }; });
            setWeekTotalMaps(maps);
            setWeekLoading({});
        });
    }, [varianceWeeks, selectedJob, drillColumns, userDataPrev, openCommitmentsPrev, costHistoryPrev]);

    // ── actions ──
    const collapse = (index) => { setCollapseNumber(index); setCollapseAll(Math.random().toString(16).substring(1)); };

    const deleteRow = (r) => {
        setDeletingRows(prev => new Set(prev).add(r.Forecast_Row_ID));
        setTimeout(() => {
            deleteUserData({ id: selectedJob, forecastGuid: selectedVersion, lockedVersion, rowId: r.Forecast_User_Data_GUID + '', tab: 'Rentals' });
            setUserData(prev => prev.filter(item => !(item.Forecast_Row_ID === r.Forecast_Row_ID && item.Forecast_Tab === 'Rentals')));
            setDeletingRows(prev => { const s = new Set(prev); s.delete(r.Forecast_Row_ID); return s; });
        }, 400);
    };

    // ── export to Excel ──
    const exportToExcel = async () => {
        try {
            setIsExporting(true);
            const ExcelJS = (await import('exceljs')).default;
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Rentals Data');
            const headers = ['', 'Current Budget', 'Actual Costs', 'Open Commitments', 'ETC', 'Total', 'Variance to Budget', 'Notes'];
            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };

            const extractRows = (obj, level = 0) => {
                const rows = [];
                if (!obj) return rows;
                const r = obj.data;
                let title = r?.title || r?.key || '';
                rows.push({
                    title: '  '.repeat(level) + title,
                    budget: r?.path === 'Phase_Code' && phaseDayZero ? (phaseDayZero[r.key] || '') : '',
                    actual: r?.actualTotal || '',
                    open: r?.openTotal || '',
                    etc: r?.etcTotal || '',
                    total: r?.totalTotal || '',
                    variance: r?.varianceBudget || '',
                    notes: r?.notes || '',
                });
                if (obj.rows && obj.rows !== false) {
                    Object.values(obj.rows).sort((a, b) => ('' + a?.data?.key).localeCompare(b?.data?.key)).forEach(child => {
                        rows.push(...extractRows(child, level + 1));
                    });
                }
                return rows;
            };

            Object.keys(objectMap).sort().forEach(key => {
                extractRows(objectMap[key]).forEach(row => {
                    worksheet.addRow([row.title, row.budget, row.actual, row.open, row.etc, row.total, row.variance, row.notes]);
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Rentals_${selectedJob}_${moment().format('YYYYMMDD')}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setIsExporting(false);
        }
    };

    // ── tabConfig for ThirdPartyRow ──
    const rentalsTabConfig = useMemo(() => ({
        tab: 'Rentals',
        defaultCostType: '52200',
        showCostTypeDropdown: false,
        costTypeDescriptions,
        stickyTitle: true,
        editableTitle: false,
        hasTCaretIndent: true,
        deferredSave: true,
        poDetailTitlePriority: 'remark',
        lineIdFallback: 'standard',
        costCodeFallback: false,
        costTypeFromData: false,
        fixedCostType: '52200',
        marginLeftOffset: 0,
        collapsableChildPadding: 22,
        newPExtraFields: null,
        budgetInit: (r) => {
            const val = budgetMap[r?.breadcrumb];
            if (val != null && val !== '' && val !== 0) return fNum(val);
            return '';
        },
        budgetDisplay: (props, r, currentBudget, userSaved) => {
            if (props.newRow) return '';
            return currentBudget;
        },
        renderDetailColumns: (r, rowData, s, fNum, moment) => (
            <View style={{ flexDirection: 'row' }}>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? moment(rowData?.Order_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? moment(rowData?.Rental_Start_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? moment(rowData?.Rental_End_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' && rowData?.Rental_Return_Date ? moment(rowData?.Rental_Return_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity_Received) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity_Open) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? rowData?.Unit_Of_Measure_Description : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Unit_Price) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'Line_ID' ? fNum(rowData?.PO_Ordered_Amount) : ''}</Text>
            </View>
        ),
    }), [budgetMap, drillColumns]);

    const phaseCCDayZero = {};

    const lookupOptions = useMemo(() => {
        const vendorSet = new Set();
        const poSet = new Set();
        [...openCommitments, ...costHistory].forEach(r => {
            if (r.Supplier_Name && r.Supplier_Name !== 'Unknown Vendor') vendorSet.add(r.Supplier_Name);
            if (r.PO_Number && r.PO_Number !== 'Unknown PO') poSet.add('' + r.PO_Number);
        });
        (userData || []).forEach(r => {
            if (r.Forecast_Tab === 'Rentals') {
                if (r.Supplier_Name) vendorSet.add(r.Supplier_Name);
                if (r.PO_Number) poSet.add('' + r.PO_Number);
            }
        });
        const phaseResCount = {};
        [...openCommitments, ...costHistory].forEach(r => {
            const phase = r.Phase_Code || r.Cost_Code || '';
            const res = r.Rental_Equipment_Classification_Tier_Name || '';
            if (phase && res) {
                if (!phaseResCount[phase]) phaseResCount[phase] = {};
                phaseResCount[phase][res] = (phaseResCount[phase][res] || 0) + 1;
            }
        });
        const phaseResourceMap = {};
        Object.keys(phaseResCount).forEach(p => {
            const best = Object.entries(phaseResCount[p]).sort((a, b) => b[1] - a[1])[0];
            if (best) phaseResourceMap[p] = best[0];
        });
        // Prefer admin-uploaded Resource Code table filtered to this page's cost types (52xxx).
        // Falls back to the project's own resource descriptions (equipment tier names) if DB is empty.
        const resourceDropdown = buildResourceDropdownFromDb({
            byCode: dbResourceCodes.byCode || {},
            codesByCostType: dbResourceCodes.codesByCostType || {},
            allowedCostTypes: PAGE_COST_TYPES,
            projectResources: resourceDescriptions || {},
            budgetByResource: {},
            fallback: resourceDescriptions || {},
        });
        return {
            phases: phaseDescriptions || {},
            costTypes: tDescriptions,
            resources: resourceDropdown,
            vendors: [...vendorSet].sort(),
            pos: [...poSet].sort(),
            reverseDisciplineMap,
            disciplineMap,
            phaseResourceMap,
            budgetPhases: Object.keys(phaseDayZero || {}),
        };
    }, [phaseDescriptions, resourceDescriptions, openCommitments, costHistory, userData, reverseDisciplineMap, disciplineMap, phaseDayZero, dbResourceCodes]);

    const pivotPopoverProps = useMemo(() => ({
        drillColumns, tabConfig: rentalsTabConfig,
        selectedJob, selectedVersion, lockedVersion,
        userData, setUserData, lookupOptions,
    }), [drillColumns, rentalsTabConfig, selectedJob, selectedVersion, lockedVersion, userData, setUserData, lookupOptions]);

    const rowContextValue = useMemo(() => ({
        tabConfig: rentalsTabConfig,
        s, path, drillColumns,
        expandedBreadcrumbs, setExpandedBreadcrumbs,
        objectMapPrev, weekTotalMaps, varianceWeeks, weekDisplayMode,
        selectedWIP, descriptionVisible,
        phaseDayZero, phaseCCDayZero, phaseDescriptions, titleDescriptions, resourceDescriptions,
        selectedJob, selectedVersion, lockedVersion,
        userData, setUserData, focusedId, indentColors,
        collapseAll, collapseNumber, setCollapseNumber,
        entriesByBreadcrumb, setPopoverBreadcrumb, pivotPopoverProps,
        poStatus, openTapDoc,
    }), [rentalsTabConfig, s, path, drillColumns, expandedBreadcrumbs, objectMapPrev, weekTotalMaps, varianceWeeks, weekDisplayMode, selectedWIP, descriptionVisible, phaseDayZero, phaseDescriptions, titleDescriptions, resourceDescriptions, selectedJob, selectedVersion, lockedVersion, userData, focusedId, collapseAll, collapseNumber, entriesByBreadcrumb, pivotPopoverProps, poStatus, openTapDoc]);

    const sortedObjectMapKeys = useMemo(() => {
        if (!objectMap) return [];
        return Object.keys(objectMap).sort((a, b) => a ? (b ? a.localeCompare(b) : -1) : 1);
    }, [objectMap]);

    const topLevelNewRow = {
        rows: false,
        data: { path: path[0], detail: {}, indent: 0, breadcrumb: 't' }
    };

    // ── ValidationArea ──
    const levelColor = (lvl) => {
        const colors = { 'Phase': '#3b82f6', 'Vendor': '#8b5cf6', 'PO': '#f59e0b', 'Line': '#10b981', 'Cost Type': '#ef4444', 'User Row': '#6366f1', 'Top Level': '#64748b' };
        return colors[lvl] || '#6b7280';
    };

    const descriptionText = (row) => [
        row.Cost_Code && row.Cost_Code !== 'undefined' && !row.Cost_Code.includes('^') ? (row.Cost_Code + (phaseDescriptions?.[row.Cost_Code] ? ' - ' + phaseDescriptions[row.Cost_Code] : '')) : null,
        row.Supplier_Name && row.Supplier_Name !== 'undefined' && !row.Supplier_Name.includes('^') ? row.Supplier_Name : null,
        row.PO_Number && row.PO_Number !== 'undefined' && !(row.PO_Number + '').includes('^') ? row.PO_Number : null,
    ].filter(Boolean).join(' \u203A ');

    const getRowLevel = (row) => {
        if (row.Equipment_Tier && row.Equipment_Tier !== '' && row.Equipment_Tier !== 'undefined') return row.Equipment_Tier;
        if (row.Forecast_Row_ID?.includes('^~u')) return 'User Row';
        if (row.Forecast_Row_ID?.includes('t^')) return 'Top Level';
        const colMap = { 'Phase_Code': row.Cost_Code, 'Supplier_Name': row.Supplier_Name, 'PO_Number': row.PO_Number, 'Cost_Type_Code': row.Cost_Type, 'Line_ID': '' };
        const labelMap = { 'Phase_Code': 'Phase', 'Supplier_Name': 'Vendor', 'PO_Number': 'PO', 'Line_ID': 'Line', 'Cost_Type_Code': 'Cost Type' };
        for (let i = drillColumns.length - 1; i >= 0; i--) {
            const val = colMap[drillColumns[i]];
            if (val && val !== 'undefined' && !val.includes('^')) return labelMap[drillColumns[i]] || drillColumns[i];
        }
        return 'Phase';
    };

    const groupedValidation = useMemo(() => {
        const groups = {};
        (sortedUserData || []).forEach(row => {
            if (row.Forecast_Tab !== 'Rentals' || !row.Value_Amount) return;
            const cols = {
                Phase_Code: row.Cost_Code || '',
                Supplier_Name: row.Supplier_Name || '',
                PO_Number: row.PO_Number || '',
            };
            let key = '';
            drillColumns.forEach(c => {
                if (cols[c]) key += '^' + cols[c];
            });
            if (!key) key = row.Forecast_Row_ID || 'unknown';
            if (!groups[key]) groups[key] = [];
            groups[key].push(row);
        });
        return groups;
    }, [sortedUserData, drillColumns]);

    const ValidationArea = () => {
        const totalAllRows = (sortedUserData || [])
            .filter(row => row.Forecast_Tab === 'Rentals' && row.Value_Amount)
            .reduce((sum, row) => sum + pNum(row.Value_Amount), 0);

        const cols = [
            { label: 'Code', w: 70 },
            { label: 'Title', w: 110 },
            { label: 'Path', w: 180 },
            { label: 'Description', w: 140 },
            { label: 'Notes', w: 130 },
            { label: 'ETC', w: 110, align: 'right' },
            { label: '', w: 40 },
        ];

        const headerRow = (cols, soft) => (
            <View style={{ flexDirection: 'row', backgroundColor: soft ? '#edf0f4' : '#1e293b', paddingHorizontal: 4, paddingLeft: 24, height: 18, alignItems: 'center' }}>
                {cols.map((col, i) => (
                    <Text key={i} style={{ width: col.w, fontSize: soft ? 10 : 11, fontWeight: '600', color: soft ? '#64748b' : '#f1f5f9', textAlign: col.align || 'left', paddingHorizontal: 8 }}>{col.label}</Text>
                ))}
            </View>
        );

        const rowBreadcrumb = (row) => {
            const colMap = { Phase_Code: row.Cost_Code || '', Supplier_Name: row.Supplier_Name || '', PO_Number: row.PO_Number || '', Cost_Type_Code: row.Cost_Type || '', Discipline: '', Resource_Code: row.Resource_Code || '', Line_ID: '' };
            let bc = '';
            for (let i = 0; i < drillColumns.length; i++) {
                const v = colMap[drillColumns[i]] || '';
                if (!v || v === 'undefined' || v.includes('^')) break;
                bc += '^' + v;
            }
            return bc;
        };

        const dataRow = (row, idx, isDuplicate) => {
            const bc = rowBreadcrumb(row);
            return (
                <View key={row.Forecast_User_Data_GUID || row.Forecast_Row_ID} style={{
                    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 4, paddingLeft: 24, height: 22,
                    backgroundColor: deletingRows.has(row.Forecast_Row_ID)
                        ? '#fee2e2'
                        : isDuplicate ? '#fef3c7' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                    borderBottom: '1px solid #f1f5f9',
                    opacity: deletingRows.has(row.Forecast_Row_ID) ? 0.5 : 1,
                    transition: 'opacity 0.3s, background-color 0.3s',
                }}>
                    <Text style={{ width: 70, fontSize: 12, color: '#475569', paddingHorizontal: 8, fontFamily: 'monospace' }}>{row.Cost_Type}</Text>
                    <Text style={{ width: 110, fontSize: 12, color: '#1e293b', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Row_Label || ''}</Text>
                    <View style={{ width: 180, paddingHorizontal: 8, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <Text style={{ fontSize: 12, color: '#1e293b', lineHeight: 18, flex: 1 }} numberOfLines={1}>{descriptionText(row)}</Text>
                        {isDuplicate && (
                            <View style={{ backgroundColor: '#f59e0b20', borderRadius: 6, paddingHorizontal: 4, paddingVertical: 1 }}>
                                <Text style={{ fontSize: 8, fontWeight: '700', color: '#d97706' }}>COMBINED</Text>
                            </View>
                        )}
                    </View>
                    <Text style={{ width: 140, fontSize: 12, color: '#475569', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Description || ''}</Text>
                    <Text style={{ width: 130, fontSize: 12, color: '#475569', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Notes || ''}</Text>
                    <Text style={{ width: 110, fontSize: 12, fontWeight: '500', color: '#1e293b', textAlign: 'right', paddingHorizontal: 8, fontFamily: 'monospace' }}>{fNum(row.Value_Amount)}</Text>
                    <View style={{ width: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <Pressable style={{ cursor: 'pointer' }} onPress={() => setPopoverBreadcrumb(bc)}>
                            <Icon3 name="MdEdit" color="#3b82f6" size={14} />
                        </Pressable>
                        <Pressable style={{ cursor: deletingRows.has(row.Forecast_Row_ID) ? 'default' : 'pointer' }}
                            onPress={() => !deletingRows.has(row.Forecast_Row_ID) && deleteRow(row)}>
                            {deletingRows.has(row.Forecast_Row_ID)
                                ? <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(0,0,0,0.1)', borderTop: '2px solid #dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                : <Icon3 name="MdDeleteOutline" color="#dc2626" size={14} />}
                        </Pressable>
                    </View>
                </View>
            );
        };

        const duplicateKeys = new Set();
        Object.entries(groupedValidation).forEach(([key, rows]) => {
            if (rows.length > 1) duplicateKeys.add(key);
        });
        const isDuplicateRow = (row) => {
            const cols = { Phase_Code: row.Cost_Code || '', Supplier_Name: row.Supplier_Name || '', PO_Number: row.PO_Number || '' };
            let key = '';
            drillColumns.forEach(c => { if (cols[c]) key += '^' + cols[c]; });
            return duplicateKeys.has(key || row.Forecast_Row_ID || '');
        };

        const flatRows = (sortedUserData || []).filter(row => row.Forecast_Tab === 'Rentals' && row.Value_Amount);

        if (phased === 'phased') {
            const grouped = {};
            flatRows.forEach(row => {
                const key = row.Cost_Code || 'Unknown';
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(row);
            });

            return (
                <View style={{ marginTop: 16 }}>
                    <View style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: 820 }}>
                        <View style={{ padding: 10, borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a' }}>ETC Values Summary</Text>
                            <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                {duplicateKeys.size > 0 ? `${duplicateKeys.size} location(s) have multiple entries (highlighted yellow)` : 'Grouped by phase code — all entries are unique'}
                            </Text>
                        </View>
                        {Object.keys(grouped).sort().map((costType, gi) => {
                            const groupRows = grouped[costType];
                            const groupSum = groupRows.reduce((sum, row) => sum + pNum(row.Value_Amount), 0);
                            return (
                                <View key={costType}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 12, backgroundColor: '#eef2ff', borderTop: gi > 0 ? '1px solid #e2e8f0' : undefined, height: 18 }}>
                                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#3730a3' }}>{costType}</Text>
                                        {phaseDescriptions?.[costType] ? <Text style={{ fontSize: 11, color: '#6366f1', marginLeft: 8 }}>{phaseDescriptions[costType]}</Text> : null}
                                    </View>
                                    {headerRow(cols, true)}
                                    {groupRows.map((row, idx) => dataRow(row, idx, isDuplicateRow(row)))}
                                    <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 4, paddingLeft: 24, height: 18, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                                        <Text style={{ width: 630, fontSize: 12, fontWeight: '600', color: '#334155', paddingHorizontal: 8 }}>Subtotal</Text>
                                        <Text style={{ width: 110, fontSize: 13, fontWeight: '700', color: '#1e293b', textAlign: 'right', paddingHorizontal: 8, fontFamily: 'monospace' }}>{fNum(groupSum)}</Text>
                                        <View style={{ width: 40 }} />
                                    </View>
                                </View>
                            );
                        })}
                        <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 4, paddingLeft: 24, backgroundColor: '#eff6ff', borderTop: '2px solid #bfdbfe', height: 18 }}>
                            <Text style={{ width: 630, fontSize: 13, fontWeight: '700', color: '#1d4ed8', paddingHorizontal: 8 }}>Grand Total</Text>
                            <Text style={{ width: 110, fontSize: 14, fontWeight: '800', color: '#1d4ed8', textAlign: 'right', paddingHorizontal: 8, fontFamily: 'monospace' }}>{fNum(totalAllRows)}</Text>
                            <View style={{ width: 40 }} />
                        </View>
                    </View>
                </View>
            );
        }

        return (
            <View style={{ marginTop: 16 }}>
                <View style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: 820 }}>
                    <View style={{ padding: 10, borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a' }}>ETC Values Summary</Text>
                        {duplicateKeys.size > 0 && (
                            <Text style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>{duplicateKeys.size} location(s) have multiple entries (highlighted)</Text>
                        )}
                    </View>
                    {headerRow(cols)}
                    {flatRows.map((row, idx) => dataRow(row, idx, isDuplicateRow(row)))}
                    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 4, paddingLeft: 24, backgroundColor: '#eff6ff', borderTop: '2px solid #bfdbfe', height: 18 }}>
                        <Text style={{ width: 630, fontSize: 13, fontWeight: '700', color: '#1d4ed8', paddingHorizontal: 8 }}>Total</Text>
                        <Text style={{ width: 110, fontSize: 14, fontWeight: '800', color: '#1d4ed8', textAlign: 'right', paddingHorizontal: 8, fontFamily: 'monospace' }}>{fNum(totalAllRows)}</Text>
                        <View style={{ width: 40 }} />
                    </View>
                </View>
            </View>
        );
    };

    // ── RENDER ──
    return (
        <View style={{ margin: 30, marginTop: 0, marginRight: 200, marginBottom: 200 }}>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            <View style={{ marginBottom: 8, flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <PivotSelector columns={drillColumns} available={availablePivotColumns} onChange={setDrillColumns} />
                <VarianceWeekSelector selectedWeeks={varianceWeeks} onChange={setVarianceWeeks} />
                <ReversedVoucherLegend tree={objectMap} />
            </View>

            <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 20, paddingTop: 0, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3, position: 'relative' }}>
                {rebuilding && (
                    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 200, alignItems: 'center', justifyContent: 'center' }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'white', padding: '8px 16px', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                            <Text style={{ fontSize: 12, fontWeight: '600', color: '#6b7280' }}>Rebuilding tree…</Text>
                        </View>
                    </View>
                )}
                {/* Sticky header */}
                <View style={{ backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 100, paddingTop: 20, width: 'max-content', minWidth: '100%' }}>
                    <View style={{ flexDirection: 'row', marginLeft: 22 }}>
                        <View style={{ flexDirection: 'row', position: 'sticky', left: 22, zIndex: 12 }}>
                            <View style={[s(300).title, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
                                <Pressable
                                    style={{ flexDirection: 'row', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 3, border: '1px solid #d1d5db', cursor: 'pointer', backgroundColor: '#f9fafb' }}
                                    onPress={() => collapse(1)}>
                                    <Icon3 name="MdUnfoldLess" size={11} color="#6b7280" />
                                    <Text style={{ color: '#6b7280', fontSize: 11, fontWeight: '600' }}>Collapse</Text>
                                </Pressable>
                            </View>
                            <View style={[s(140).title, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }]}>
                                <Text style={{ fontSize: 12 }}>Description</Text>
                                <Icon2
                                    onPress={() => setDescriptionVisible(!descriptionVisible)}
                                    name={descriptionVisible ? 'chevron-down-circle-outline' : 'chevron-forward-circle-outline'}
                                    size={16}
                                />
                            </View>
                        </View>
                        {descriptionVisible ? <View style={{ flexDirection: 'row' }}>
                            <Text style={s().title}>Order Date</Text>
                            <Text style={s().title}>Start Date</Text>
                            <Text style={s().title}>End Date</Text>
                            <Text style={s().title}>Return Date</Text>
                            <Text style={s().title}>Qty Ordered</Text>
                            <Text style={s().title}>Qty Received</Text>
                            <Text style={s().title}>Qty Open</Text>
                            <Text style={s().title}>UoM</Text>
                            <Text style={s().title}>Unit Price</Text>
                            <Text style={s().title}>PO Amount</Text>
                            <Text style={s().title}>Invoice</Text>
                        </View> : null}
                        <View style={{ width: 10 }} />
                        <Text style={s().title}>Current Budget</Text>
                        <View style={{ width: 10 }} />
                        <Text style={s().title}>Most Recent Cost Date</Text>
                        <Text style={s().title}>Actual Costs</Text>
                        <Text style={s().title}>Open</Text>
                        <Text style={s(120).title}>Estimate to Complete (ETC)</Text>
                        <Text style={s().title}>Total</Text>
                        <Text style={s().title}>Variance to Budget (+)/-</Text>
                        {varianceWeeks.map(guid => (
                            <View key={guid} style={[s().title, { backgroundColor: '#eef2ff', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }]}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                    <Text style={{ color: '#4338ca', fontSize: 12, fontWeight: '700' }}>{getWeekLabel(guid)}</Text>
                                    {weekLoading[guid] ? <Text style={{ fontSize: 10, color: '#818cf8', animation: 'spin 1s linear infinite' }}>⟳</Text> : null}
                                </View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                    <Pressable onPress={() => setWeekDisplayMode(prev => ({ ...prev, [guid]: 'total' }))} style={{ cursor: 'pointer', backgroundColor: (weekDisplayMode[guid] || 'var') === 'total' ? '#4338ca' : '#e0e7ff', borderRadius: 3, padding: '1px 4px' }}>
                                        <Text style={{ fontSize: 8, fontWeight: '700', color: (weekDisplayMode[guid] || 'var') === 'total' ? 'white' : '#6366f1' }}>TOT</Text>
                                    </Pressable>
                                    <Pressable onPress={() => setWeekDisplayMode(prev => ({ ...prev, [guid]: 'var' }))} style={{ cursor: 'pointer', backgroundColor: (weekDisplayMode[guid] || 'var') === 'var' ? '#4338ca' : '#e0e7ff', borderRadius: 3, padding: '1px 4px' }}>
                                        <Text style={{ fontSize: 8, fontWeight: '700', color: (weekDisplayMode[guid] || 'var') === 'var' ? 'white' : '#6366f1' }}>VAR</Text>
                                    </Pressable>
                                </View>
                            </View>
                        ))}
                        <Text style={s(120).title}>Cost Type</Text>
                        <View style={{ flexDirection: 'row' }}>
                            <Text style={s(400).title}>Notes</Text>
                            <Pressable onPress={() => !isExporting && exportToExcel()}>
                                <Text style={{ backgroundColor: isExporting ? '#c0c0c0' : '#4CAF50', color: 'white', borderRadius: 2, textAlign: 'center', padding: 5, margin: 2, fontSize: 12 }}>{isExporting ? 'EXPORTING...' : 'EXPORT'}</Text>
                            </Pressable>
                        </View>
                    </View>

                    {/* Totals row */}
                    <View style={{ flexDirection: 'row', marginLeft: 22 }}>
                        <View style={{ position: 'sticky', left: 22, zIndex: 12, backgroundColor: 'white' }}>
                            <Text style={[s(descriptionVisible ? 1441 : 441).notEdit, { textAlign: 'left', backgroundColor: '#f0f0f0' }]}>Totals</Text>
                        </View>
                        <View style={{ width: 10, backgroundColor: 'white' }} />
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{totalCurrentBudget}</Text>
                        <View style={{ width: 110, backgroundColor: 'white' }} />
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{actualTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{openTotalTotal}</Text>
                        <Text style={[s(120).notEdit, { backgroundColor: '#f0f0f0' }]}>{etcTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{totalTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{varianceBudgetTotal}</Text>
                        {varianceWeeks.map(guid => {
                            const gt = guid === selectedWIP?.Version_GUID ? prevGrandTotal : weekTotalMaps[guid]?.grandTotal;
                            const mode = weekDisplayMode[guid] || 'var';
                            const displayGt = mode === 'var' && gt != null ? pNum(totalTotalTotal) - pNum(gt) : gt;
                            return (
                                <Text key={guid} style={[s().notEdit, { backgroundColor: mode === 'var' ? '#fef9ee' : '#eef2ff' }]}>
                                    {displayGt != null ? fNum(displayGt) : ''}
                                </Text>
                            );
                        })}
                    </View>
                </View>

                {/* Tree rows */}
                <ThirdPartyRowContext.Provider value={rowContextValue}>
                    <PopoverBreadcrumbContext.Provider value={popoverBreadcrumb}>
                        {sortedObjectMapKeys.map(phase => (
                            <View key={phase} style={{ marginTop: 10 }}>
                                {phase.includes('t^')
                                    ? <View style={{ marginLeft: 22 }}><ThirdPartyRow data={objectMap[phase]} /></View>
                                    : <PivotRowCollapsable data={objectMap[phase]} />
                                }
                            </View>
                        ))}
                        <View style={{ marginTop: 10 }}>
                            <View style={{ marginLeft: 22 }}><ThirdPartyRow newRow={true} data={topLevelNewRow} /></View>
                        </View>
                        <EtcPopoverHost
                            objectMap={objectMap}
                            popoverBreadcrumb={popoverBreadcrumb}
                            setPopoverBreadcrumb={setPopoverBreadcrumb}
                            entriesByBreadcrumb={entriesByBreadcrumb}
                            pivotPopoverProps={pivotPopoverProps}
                            topLevelNodeData={topLevelNewRow.data}
                        />
                    </PopoverBreadcrumbContext.Provider>
                </ThirdPartyRowContext.Provider>
            </View>

            <ValidationArea />
            <AuditHistoryPanel tabKey="rentals" />
            {viewerNode}
        </View>
    );
};

export default Rentals;
